/**
 * Statistics — the arithmetic, kept out of both the SQL and the components.
 *
 * design/README.md §11 is unusually prescriptive here and the constraint is the interesting part:
 * **bars only. No pies, no areas, no second accent series.** So there is no chart library and no
 * scale abstraction; there are four measures, each of which is a list of numbers and a maximum.
 *
 * The header note — `MEAN 7.61 · MEDIAN 8 · MODE 8` — is three different claims about the same
 * data, and they disagree on purpose. A mean of 7.61 with a mode of 8 says most things are an 8
 * and a tail drags the average down; that is the sentence the note exists to make.
 */
export const STATS_PERIODS = [
    { period: "all", label: "ALL TIME" },
    { period: "year", label: "THIS YEAR" },
    { period: "90-days", label: "LAST 90 DAYS" },
    { period: "30-days", label: "LAST 30 DAYS" },
];
const DAY_MS = 24 * 60 * 60 * 1000;
/** The instant a period begins, or null for all time. Same shape as the MOVE baselines. */
export function periodStart(period, now) {
    switch (period) {
        case "all":
            return null;
        case "year":
            return new Date(Date.UTC(now.getUTCFullYear(), 0, 1));
        case "90-days":
            return new Date(now.getTime() - 90 * DAY_MS);
        case "30-days":
            return new Date(now.getTime() - 30 * DAY_MS);
    }
}
/**
 * The 1–10 histogram.
 *
 * Always ten buckets, even where a score is unused: a histogram that drops its empty columns is
 * not a histogram, it is a bar chart with a misleading x-axis — and "nobody in this library has
 * ever given a 1" is one of the more interesting things the chart can say.
 */
export function distribution(values) {
    const counts = Array.from({ length: 10 }, () => 0);
    for (const value of values) {
        if (Number.isInteger(value) && value >= 1 && value <= 10)
            counts[value - 1] = counts[value - 1] + 1;
    }
    const total = counts.reduce((a, b) => a + b, 0);
    if (total === 0)
        return { counts, total: 0, mean: null, median: null, mode: null, peak: 0 };
    const sum = counts.reduce((acc, n, i) => acc + n * (i + 1), 0);
    const peak = Math.max(...counts);
    /*
     * The mode is the FIRST bucket at the peak, and ties are not broken by anything cleverer.
     * With integer scores over a few thousand songs an exact tie is rare, and inventing a rule to
     * resolve it would mean the header note changed for a reason nobody could see.
     */
    const mode = counts.indexOf(peak) + 1;
    return { counts, total, mean: sum / total, median: medianOf(counts, total), mode, peak };
}
/**
 * The median from the buckets, without materialising the list.
 *
 * On an even count this takes the lower of the two middles rather than averaging them, because
 * the scale has no halves — a median of 7.5 is not a rating anyone gave, and it would sit in the
 * header next to a mode of 8 looking like a rounding bug.
 */
function medianOf(counts, total) {
    const target = Math.ceil(total / 2);
    let seen = 0;
    for (let i = 0; i < counts.length; i += 1) {
        seen += counts[i];
        if (seen >= target)
            return i + 1;
    }
    return counts.length;
}
/**
 * Sorts and trims a grouped measure for display.
 *
 * Buckets with nothing rated in them keep their place and plot no bar. Dropping them would make
 * "the 1960s, of which you have rated nothing" invisible, and the gap in the chart is the point.
 */
export function rankMeasure(measures, limit = 12) {
    return [...measures]
        .sort((a, b) => (b.average ?? -1) - (a.average ?? -1) || b.count - a.count || a.label.localeCompare(b.label))
        .slice(0, limit);
}
/** `92%`, as a whole number. Zero songs is 0%, not NaN and not 100%. */
export function coverageRatio(coverage) {
    if (coverage.inLibrary === 0)
        return 0;
    return coverage.rated / coverage.inLibrary;
}
//# sourceMappingURL=statistics.js.map